import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { CompositeModel } from '../complex/composite.model';
import { DropdownModel } from '../complex/dropdown.model';
import { TextModelOptions } from '../options/text-model-options';
import { TextModel } from '../primitive/text.model';
import {
    JsonNotificationIdentifierModel,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
} from './notification-identifier.model';

export class ExpenseModel extends CompositeModel {
    public expenseCode: DropdownModel<TextModel>;
    public expenseValue: TextModel;
    public constructor(
        public notification: NotificationIdentifierModel,
        expenseValue = '',
    ) {
        super('ExpenseModel');

        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const configService = injector.get(ConfigService);
        const valueHelperService = injector.get(ValueHelperService);
        const options = valueHelperService.getExpenseType().map((vh) => {
            return new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.descText, vh.code));
        });
        this.expenseCode = <DropdownModel<TextModel>>this.set(new DropdownModel('Type', options));
        modelOptionsLoader.load(this.expenseCode, 'ConfirmationExpense', 'ExpenseType');
        this.expenseValue = <TextModel>this.set(
            new TextModel(
                'Amount',
                expenseValue,
                new TextModelOptions({
                    unit: configService.getExpenseCurrency(),
                    inputType: ConfigConstants.inputTypeNumeric,
                }),
            ),
        );
        // the expenseValue is a currency => this will contain a regex pattern for the text input.
        // for more details refer to https://regex101.com/r/JDPY0A/5
        modelOptionsLoader.load(this.expenseValue, 'ConfirmationExpense', 'Amount');
    }

    public getOutModel(): ODataExpenseOutModel {
        const outModel = <ODataExpenseOutModel>super.getOutModel();
        outModel.NotificationNo = this.notification.qmNum;
        outModel.TaskNumber = this.notification.maNum;

        return outModel;
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns {any}
     */
    public toJSON(): unknown {
        const jsonModel = {
            notification: this.notification,
            expenseCode: this.expenseCode.selectedEntries.map((entry) => entry.value),
            expenseValue: this.expenseValue.value,
        };
        return jsonModel;
    }
}

export class ExpenseModelFromJson extends ExpenseModel {
    public constructor(data: JsonExpenseModel) {
        super(new NotificationIdentifierModelFromJson(data.notification), data.expenseValue);
        data.expenseCode.forEach((value) => {
            this.expenseCode.selectByPredicate((entry) => entry.value === value);
        });
    }
}

export interface JsonExpenseModel {
    notification: JsonNotificationIdentifierModel;
    expenseCode: string[];
    expenseValue: string;
}

export interface ODataExpenseOutModel extends ODataObject {
    Type: string;
    Amount: string;
    NotificationNo: string;
    TaskNumber: string;
}
